import { chargeCellMs, chargeCells, chargeFullMs } from "../animations/charge";
import { hornBlasts, isPlainAlert } from "../animations/horn";
import {
  recordBuildMs,
  recordBuildTickMs,
  recordBuildTicks,
} from "../animations/recordBuild";
import {
  targetFlyMs,
  targetFullMs,
  targetMarkMs,
  targetMarks,
  targetSweepStartMs,
} from "../animations/targetCharge";
import { bigDealAmount, soundAccent } from "../constants";
import {
  bell,
  readSetting,
  readUrlSetting,
  sweep,
  tone,
  withAccent,
  writeSetting,
} from "./audioEngine";
import { hasSample, playSample } from "./samples";
import { feedLandMs, feedPopMs } from "../animations/targetFeed";

// Welk geluid er bij welke animatie hoort; een animatie die hier niet staat is stil.
// Een set vult aan op de basis; kiezen in het animatielab of met ?soundset=... (zie fire).
// Harder of zachter: de gain per geluid, of soundVolume in constants.js. Tijden in seconden.

// De tikken lopen exact mee met de blokjes uit animations/charge.js.
const tickAt = (index) => chargeCellMs(index) / 1000;
const fullAt = chargeFullMs / 1000;

// Per blokje van de aanloop: index, positie van 0 tot 1 en tijdstip.
function eachCell(run) {
  for (let i = 0; i < chargeCells; i += 1) run(i, i / (chargeCells - 1), tickAt(i));
}

// De aanloop: een tik per blokje, steeds sneller en hoger; de eerste iets zachter.
function ticks({ type = "square", freq = 300, rise = 900, gain = 0.09 }) {
  eachCell((i, part, at) =>
    tone({
      at,
      freq: freq + part * part * rise,
      type,
      duration: 0.05,
      gain: gain * (0.78 + 0.22 * part),
    }),
  );
}

// De basis: warm, met klokjes.

function charge() {
  ticks({});
  tone({ at: 0, freq: 110, to: 440, type: "triangle", duration: fullAt, gain: 0.1 });
  // Het opzuigen vlak voor de flits.
  sweep({ at: fullAt - 0.3, duration: 0.32, gain: 0.1, from: 400, to: 5000, type: "highpass" });
}

// De aankondiging: de klap waarmee het beeld donker wordt, dan vier tonen omhoog.
function splash() {
  sweep({ at: 0, duration: 0.5, gain: 0.3, from: 1800, to: 120 });
  tone({ at: 0, freq: 90, to: 44, type: "sine", duration: 0.6, gain: 0.32 });
  [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) =>
    bell({ at: 0.08 + i * 0.11, freq, duration: 1.2, gain: 0.2 }),
  );
}

// Het seintje: eerst een klokje (een begin hoor je, een zwelling niet), dan een lage zwelling.
function glow() {
  bell({ at: 0, freq: 880, duration: 0.7, gain: 0.16 });
  bell({ at: 0.12, freq: 1174.7, duration: 0.9, gain: 0.1 });

  tone({ at: 0, freq: 196, type: "sine", duration: 1.5, gain: 0.11, attack: 0.18 });
  tone({ at: 0.08, freq: 294, type: "sine", duration: 1.2, gain: 0.07, attack: 0.18 });
  tone({ at: 0.08, freq: 392, type: "sine", duration: 0.9, gain: 0.035, attack: 0.18 });
}

// Noten als hele verhoudingen van één grondtoon, zodat de boventonen niet zweven.
// Zelfde reden als in tools/geluiden.py: anders klinkt het vals zodra de tv hard staat.
const fanfareRoot = 130.8125;

// Tonen op veelvouden van fanfareRoot, elk spread seconden na de vorige.
function chord(ratios, { at, spread = 0, ...rest }) {
  ratios.forEach((ratio, i) => tone({ at: at + i * spread, freq: fanfareRoot * ratio, ...rest }));
}

// Het record: fonkelingen in de maat van de vlakjes die openspringen, geen knallen.
function fireworks() {
  // Het akkoord dat blijft staan zolang de kaart er is.
  tone({ at: 0.04, freq: fanfareRoot, type: "sine", duration: 2.8, gain: 0.12, attack: 0.04 });
  chord([2, 2.5, 3, 4], {
    at: 0.06,
    spread: 0.02,
    type: "triangle",
    duration: 2.6,
    gain: 0.1,
    attack: 0.05,
  });

  // Per vlakje een tikje en een hoge toon die blijft hangen.
  const glans = [8, 10, 12, 16];
  [0, 0.3, 0.6, 0.95, 1.3, 1.7, 2.1, 2.5].forEach((at, i) => {
    tone({
      at,
      freq: fanfareRoot * glans[i % glans.length],
      type: "sine",
      duration: 1.1,
      gain: 0.11 - i * 0.008,
    });
    sweep({ at, duration: 0.03, gain: 0.03, from: 7000, to: 4000, type: "bandpass", q: 3 });
  });

  // Een veeg die met het akkoord meekomt en wegzakt, zoals een bekken.
  sweep({ at: 0.04, duration: 1.8, gain: 0.07, from: 5000, to: 9000, type: "highpass" });
}

// Het eigen record: als het target, maar een octaaf lager en korter; trots, geen feestje.
function personalBest() {
  tone({ at: 0, freq: fanfareRoot * 3, type: "triangle", duration: 0.45, gain: 0.12 });
  tone({ at: 0.16, freq: fanfareRoot * 4, type: "triangle", duration: 0.5, gain: 0.13 });

  const at = 0.34;
  tone({ at, freq: fanfareRoot, type: "sine", duration: 2, gain: 0.11, attack: 0.04 });
  chord([2, 2.5, 3, 4], {
    at,
    spread: 0.014,
    type: "triangle",
    duration: 1.9,
    gain: 0.1,
    attack: 0.04,
  });

  [8, 10, 12].forEach((ratio, i) =>
    tone({
      at: at + 0.1 + i * 0.22,
      freq: fanfareRoot * ratio,
      type: "sine",
      duration: 1.1,
      gain: 0.05 - i * 0.008,
    }),
  );

  sweep({ at: at - 0.04, duration: 1.4, gain: 0.05, from: 6000, to: 9000, type: "highpass" });
}

// Het teamtarget: een korte fanfare. Geen bell(), die ontstemt zijn boventoon expres.
function target() {
  chord([3, 4, 5], { at: 0, spread: 0.19, type: "triangle", duration: 0.55, gain: 0.15 });

  // Het slotakkoord, met een kale sinus als grondtoon.
  const at = 0.58;
  tone({ at, freq: fanfareRoot, type: "sine", duration: 2.4, gain: 0.13, attack: 0.03 });
  chord([4, 5, 6, 8], {
    at,
    spread: 0.012,
    type: "triangle",
    duration: 2.3,
    gain: 0.12,
    attack: 0.03,
  });
  chord([8, 10, 12], { at: at + 0.02, spread: 0.05, type: "sine", duration: 1.6, gain: 0.05 });

  sweep({ at: at - 0.06, duration: 1.8, gain: 0.07, from: 5200, to: 9000, type: "highpass" });
}

// De meter naar 100: een tik per 5% in het tempo van de wijzer (animations/targetCharge.js),
// en op 100 een klap en een akkoord in de toonsoort van de fanfare.
const targetMarkAt = (index) => targetMarkMs(index / targetMarks) / 1000;
const targetFullAt = targetFullMs / 1000;

function targetCharge() {
  // De meter stijgt op uit de hero: een zwiep omhoog en een toon die meezakt met de wijzer.
  const fly = targetFlyMs / 1000;
  sweep({ at: 0, duration: fly, gain: 0.08, from: 500, to: 4200, type: "bandpass", q: 2 });
  tone({ at: 0, freq: 392, to: 98, type: "triangle", duration: fly, gain: 0.06 });

  for (let i = 1; i <= targetMarks; i += 1) {
    const part = i / targetMarks;
    tone({
      at: targetMarkAt(i),
      freq: 280 + part * part * 820,
      type: "square",
      duration: 0.045,
      gain: 0.07 * (0.75 + 0.25 * part),
    });
  }

  const from = targetSweepStartMs / 1000;
  tone({
    at: from,
    freq: 98,
    to: 392,
    type: "triangle",
    duration: targetFullAt - from,
    gain: 0.1,
  });
  sweep({ at: targetFullAt - 0.35, duration: 0.36, gain: 0.1, from: 400, to: 6000, type: "highpass" });

  // De klap op 100.
  sweep({ at: targetFullAt, duration: 0.5, gain: 0.22, from: 3000, to: 120 });
  chord([2, 3, 4], { at: targetFullAt, type: "triangle", duration: 0.9, gain: 0.1 });
}

// De stempel: een doffe klap als hij neerkomt (0,2s, zie fx-stamp-slam) en twee tonen omhoog.
function recordStamp() {
  const hit = 0.2;
  sweep({ at: hit, duration: 0.35, gain: 0.28, from: 1400, to: 80 });
  tone({ at: hit, freq: 110, to: 55, type: "sine", duration: 0.4, gain: 0.22 });
  tone({ at: hit + 0.12, freq: fanfareRoot * 4, type: "triangle", duration: 0.25, gain: 0.09 });
  tone({ at: hit + 0.26, freq: fanfareRoot * 6, type: "triangle", duration: 0.5, gain: 0.09 });
}

// De opbouw naar de stempel (animations/recordBuild.js): aanzwellen en steeds snellere,
// hogere tikken, tot waar de stempel neerkomt.
const recordBuildTones = [2, 2.5, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24];
const buildAt = recordBuildMs / 1000;

// attack net korter dan duration: aanzwellen tot de klap en dan meteen weg.
const swell = { duration: buildAt + 0.08, attack: buildAt - 0.02 };

function recordBuild() {
  tone({ at: 0, freq: fanfareRoot, to: fanfareRoot * 2, type: "sine", gain: 0.1, ...swell });
  tone({ at: 0, freq: fanfareRoot * 2, to: fanfareRoot * 4, type: "triangle", gain: 0.05, ...swell });
  sweep({ at: 0, from: 500, to: 7000, type: "highpass", gain: 0.07, ...swell });

  for (let i = 0; i < recordBuildTicks; i += 1) {
    const part = i / (recordBuildTicks - 1);
    tone({
      at: recordBuildTickMs(i) / 1000,
      freq: fanfareRoot * recordBuildTones[i % recordBuildTones.length],
      type: "triangle",
      duration: 0.07,
      gain: 0.035 + part * 0.05,
    });
  }
}

// Een confettisalvo: plop, geritsel en een fonkeling. Kort, want er kunnen er drie komen
// (saleGroup in hooks/useSaleEvents.js).
function saleConfetti() {
  sweep({ at: 0, duration: 0.14, gain: 0.16, from: 1200, to: 150 });
  tone({ at: 0, freq: 160, to: 70, type: "sine", duration: 0.16, gain: 0.14 });
  sweep({ at: 0.03, duration: 0.7, gain: 0.05, from: 9000, to: 3500, type: "highpass", attack: 0.08 });
  chord([12, 16], { at: 0.08, spread: 0.09, type: "sine", duration: 0.5, gain: 0.04 });
}

// Een stijger: twee tonen omhoog, meer niet.
function rankUp() {
  tone({ at: 0, freq: 587.33, type: "triangle", duration: 0.22, gain: 0.11 });
  tone({ at: 0.14, freq: 880, type: "triangle", duration: 0.34, gain: 0.11 });
}

// "Verkopen gemist", bij het aanzetten van de tv: rustig, twee klokjes.
function missed() {
  bell({ at: 0, freq: 523.25, duration: 1.4, gain: 0.12 });
  bell({ at: 0.24, freq: 783.99, duration: 1.8, gain: 0.1 });
}

// De toeter zonder toeter.mp3: per stoot twee zaagtanden een kleine terts uit elkaar,
// elk met een net ontstemde tweede. Het schuren is hier juist de bedoeling.
function airhorn() {
  hornBlasts.forEach(([start, end]) => {
    const at = start / 1000;
    const duration = (end - start) / 1000;
    [415.3, 417, 493.9, 496].forEach((freq) =>
      tone({ at, freq, type: "sawtooth", duration, gain: 0.05, attack: 0.012 }),
    );
  });
}

// Geld naar de meter (animations/targetFeed.js): een munt die opspringt, een zwiep die
// meegaat naar de meter, een klap bij de landing, en een toon die omhoog blijft trekken
// tot de aanloop naar 100 het overneemt.
const feedPop = feedPopMs / 1000;
const feedLand = feedLandMs / 1000;

function targetFeed() {
  bell({ at: 0, freq: 1568, duration: 0.5, gain: 0.14 });
  bell({ at: 0.06, freq: 2093, duration: 0.7, gain: 0.1 });

  const flight = feedLand - feedPop;
  sweep({ at: feedPop, duration: flight, gain: 0.12, from: 500, to: 3500, type: "bandpass", q: 2, attack: flight * 0.8 });
  tone({ at: feedPop, freq: 220, to: 880, type: "triangle", duration: flight, gain: 0.08, attack: flight * 0.7 });

  sweep({ at: feedLand, duration: 0.35, gain: 0.26, from: 1400, to: 90 });
  tone({ at: feedLand, freq: 110, to: 55, type: "sine", duration: 0.4, gain: 0.22 });
  chord([4, 5, 6], { at: feedLand, spread: 0.03, type: "triangle", duration: 0.7, gain: 0.08 });
  tone({ at: feedLand + 0.1, freq: 98, to: 196, type: "sawtooth", duration: 0.7, gain: 0.04, attack: 0.5 });
}

const base = {
  "target-feed": targetFeed,
  "sale-charge": charge,
  "sale-splash": splash,
  "screen-glow": glow,
  fireworks,
  "personal-best": personalBest,
  "target-charge": targetCharge,
  "target-hit": target,
  "rank-up": rankUp,
  "sale-confetti": saleConfetti,
  "record-stamp": recordStamp,
  "record-build": recordBuild,
  "missed-sales": missed,
};

// Zacht: laag en rustig, geen klappen of blokgolven.

function softCharge() {
  // Een sinus draagt minder ver, dus iets luider en met een tweede toon erbij.
  ticks({ type: "sine", freq: 440, rise: 500, gain: 0.085 });
  ticks({ type: "triangle", freq: 880, rise: 1000, gain: 0.03 });
  tone({ at: 0, freq: 147, to: 294, type: "sine", duration: fullAt, gain: 0.09 });
}

function softSplash() {
  // Geen klap: alleen een akkoord dat blijft hangen.
  [392, 493.88, 587.33, 783.99].forEach((freq, i) =>
    bell({ at: i * 0.14, freq, duration: 1.8, gain: 0.12 }),
  );
}

const sets = {
  zacht: {
    name: "Zacht",
    note: "laag en rustig, voor een stille ruimte",
    cues: {
      "sale-charge": softCharge,
      "sale-splash": softSplash,
    },
  },
};

// Voor het labpaneel, in deze volgorde.
export const soundSets = Object.entries(sets).map(([id, set]) => ({
  id,
  name: set.name,
  note: set.note,
}));

const storeKey = "tdSoundSet";
const ids = soundSets.map((set) => set.id);

// Standaard de rustigste set.
const fallback = "zacht";

let chosen = null;

export function soundSet() {
  if (chosen) return chosen;
  const saved = readUrlSetting("soundset", ids, storeKey) || readSetting(storeKey);
  chosen = ids.includes(saved) ? saved : fallback;
  return chosen;
}

export function setSoundSet(id, { remember = true } = {}) {
  if (!ids.includes(id)) return;
  chosen = id;
  if (remember) writeSetting(storeKey, id);
}

// Vaste geluiden: de set kiest niets, jij kiest het bestand (sampleOptions in samples.js).
const fixed = ["fireworks", "personal-best", "sale-confetti", "sale-attention"];

// De momenten die boven de rest uit mogen komen, met soundAccent uit constants.js.
const accented = ["screen-glow", "sale-charge", "sale-splash", "target-feed", "target-charge"];

// Het toeterseintje (Film in het lab): niet bij Oud.
const tooting = () => !isPlainAlert();

// De toeter klinkt bovenop het geluid van de set, bij het seintje.
// Zonder soundAccent: de toeter is al hard, en op vol volume gaat de begrenzer knijpen.
function toot(id) {
  if (id !== "screen-glow" || !tooting()) return;
  if (!playSample("sale-horn")) airhorn();
}

// Bij Film: een trailerversie in plaats van het gewone geluid, de eerste die klaarstaat.
// Ontbreken ze allemaal, dan klinkt gewoon wat er anders zou klinken.
const epicSamples = {
  "sale-charge": (payload) =>
    payload.amount >= bigDealAmount
      ? ["sale-charge-huge", "sale-charge-epic"]
      : ["sale-charge-epic"],
  // Met stem als de stemmen aan staan, anders dezelfde aanloop zonder.
  "target-charge": () => ["target-charge-epic", "target-charge-bare"],
  "record-build": () => ["record-build-epic"],
  "record-stamp": (payload) => [payload.kind === "record" ? "record-voice" : "best-voice"],
};

function playEpic(id, payload) {
  const pick = epicSamples[id];
  return Boolean(pick) && tooting() && pick(payload).some((sample) => playSample(sample));
}

// Vuurwerk en eigen record na de stempel: bij Film zit hun slot al in de stem.
const voiceFor = { fireworks: "record-voice", "personal-best": "best-voice" };

function coveredByVoice(id, payload) {
  return Boolean(voiceFor[id] && payload.afterStamp && tooting() && hasSample(voiceFor[id]));
}

// Eén geluid uit één set: eerst een vast bestand, dan wat de set zelf heeft,
// dan een bestand uit public/sounds/, en anders de basis.
function fire(id, setId, payload = {}) {
  const set = sets[setId] || sets[fallback];
  const accent = accented.includes(id) ? soundAccent : 1;
  const loud = accent;

  toot(id);
  if (playEpic(id, payload) || coveredByVoice(id, payload)) return;

  if (fixed.includes(id) && playSample(id, loud)) return;

  const cue = set.cues[id];
  if (cue) withAccent(accent, cue);
  else if (!playSample(id, loud) && base[id]) withAccent(accent, base[id]);
}

// Het geluid bij een animatie, als die er een heeft; de engine checkt zelf of geluid aan staat.
// payload is die van de animatie, voor geluiden die ervan afhangen (de stem op de stempel).
export function playCue(id, payload) {
  fire(id, soundSet(), payload);
}

// Een set voorproeven zoals op de tv: de aanloop en daarna de aankondiging.
export function previewSoundSet(id) {
  fire("sale-charge", id);
  setTimeout(() => fire("sale-splash", id), chargeFullMs);
}
